import React, { useState, useEffect } from "react";
import useClipboard from "../hooks/useClipboard";
import "./CopyButton.css";

/**
 * A component for copying ShareURL parameters from URLs
 *
 * @param {Object} props - Component props
 * @param {string} props.url - The full URL to extract from (defaults to current URL)
 * @param {string} props.paramName - Parameter name to extract (defaults to 'shareUrl')
 * @param {string} props.className - Additional CSS class
 * @param {string} props.buttonText - Button display text
 */
const ShareUrlButton = ({
  url = window.location.href,
  paramName = "shareUrl",
  className = "",
  buttonText = "Copy Shared Link",
  ...props
}) => {
  const { copyUrlParam, copyToClipboard, extractUrlParam, copyStatus } =
    useClipboard();
  const [showModal, setShowModal] = useState(false);
  const [extractedUrl, setExtractedUrl] = useState(null);
  const [error, setError] = useState(null);

  // Extract the parameter value when URL or paramName changes
  useEffect(() => {
    try {
      const extracted = extractUrlParam(url, paramName);
      setExtractedUrl(extracted);
      setError(null);
    } catch (err) {
      console.error("Error extracting URL parameter:", err);
      setExtractedUrl(null);
      setError(`Failed to extract parameter: ${err.message}`);
    }
  }, [url, paramName, extractUrlParam]);

  const handleCopy = async () => {
    try {
      // Try to extract and copy the shareUrl parameter
      const success = await copyUrlParam(
        url,
        paramName,
        () => {
          // On success
          setError(null);
        },
        (error) => {
          // On error from param extraction
          setError(`Could not extract ${paramName} parameter: ${error}`);
          setShowModal(true);
        }
      );

      // If parameter extraction failed, show modal with full URL option
      if (!success) {
        setShowModal(true);
      }
    } catch (err) {
      console.error("Error in ShareUrlButton:", err);
      setError("Failed to process URL");
      setShowModal(true);
    }
  };

  // Close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  // Copy the full URL as fallback
  const copyFullUrl = async () => {
    await copyToClipboard(url);
    closeModal();
  };

  // Generate a button label
  const getButtonLabel = () => {
    if (copyStatus.copied) return "Copied!";
    if (extractedUrl) return `Copy ${paramName}`;
    return buttonText || "Copy URL";
  };

  return (
    <>
      <div className={`share-url-container ${!extractedUrl ? "no-param" : ""}`}>
        <button
          className={`copy-button ${className} ${
            copyStatus.copied ? "success" : ""
          } ${copyStatus.error ? "error" : ""}`}
          onClick={handleCopy}
          disabled={!extractedUrl && !url}
          title={
            extractedUrl
              ? `Copy: ${extractedUrl}`
              : `No ${paramName} parameter found`
          }
          {...props}
        >
          {getButtonLabel()}
        </button>

        {extractedUrl && (
          <div className="param-preview">
            <span className="param-name">{paramName}:</span>
            <span className="param-value" title={extractedUrl}>
              {extractedUrl.length > 40
                ? extractedUrl.substr(0, 37) + "..."
                : extractedUrl}
            </span>
          </div>
        )}

        {!extractedUrl && url && (
          <div className="param-preview error">
            <span className="param-value">No {paramName} parameter found</span>
          </div>
        )}
      </div>

      {/* Modal for error handling and manual options */}
      {showModal && (
        <div className="copy-modal-backdrop" onClick={closeModal}>
          <div
            className="copy-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="copy-modal-close" onClick={closeModal}>
              ×
            </button>

            <h3>Copy Link</h3>
            {error && <p className="error-message">{error}</p>}

            <p>Choose what to copy:</p>

            <div className="copy-options">
              {extractedUrl && (
                <button
                  className="button"
                  onClick={() => {
                    copyToClipboard(extractedUrl);
                    closeModal();
                  }}
                >
                  Copy {paramName} Value
                </button>
              )}

              <button className="button button-secondary" onClick={copyFullUrl}>
                Copy Full URL
              </button>
            </div>

            <div className="url-preview">
              <p>
                <strong>Current URL:</strong>
              </p>
              <div className="url-box">{url}</div>

              {extractedUrl && (
                <>
                  <p>
                    <strong>Extracted {paramName}:</strong>
                  </p>
                  <div className="url-box extracted">{extractedUrl}</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareUrlButton;
